import React from 'react';
import { Icon } from '@phosphor-icons/react';
import Button from 'src/components/ui/Button';
import { formatBytes, truncateMiddle } from '../utils';

type FileRowSize = 'sm' | 'xs';

interface FileRowProps {
  name: string;
  size: number;
  /** Phosphor Icon component — FileRow controls sizing and colour. */
  icon?: Icon;
  /** Pre-rendered node for dynamic icons (e.g. status-dependent spinners/checks). */
  iconNode?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Phosphor icon rendered as a small ghost action button on the right. */
  actionIcon?: Icon;
  actionLabel?: string;
  onAction?: () => void;
  /** When provided, the action renders as an anchor tag opening in a new tab. */
  actionHref?: string;
  /** 'sm' (default) uses text-sm / text-xs; 'xs' uses text-xs for compact contexts. */
  textSize?: FileRowSize;
}

export default function FileRow({
  name,
  size,
  icon: IconComponent,
  iconNode,
  subtitle,
  actionIcon: ActionIcon,
  actionLabel,
  onAction,
  actionHref,
  textSize = 'sm'
}: FileRowProps) {
  const nameClass =
    textSize === 'xs'
      ? 'text-xs text-content-default whitespace-nowrap'
      : 'text-sm text-content-default whitespace-nowrap';
  const metaClass = 'text-xs text-content-subtlest';

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border-subtle bg-white">
      {IconComponent ? (
        <IconComponent weight="light" size={textSize === 'xs' ? 14 : 16} className="shrink-0 text-content-subtle" />
      ) : (
        <span className="shrink-0">{iconNode}</span>
      )}
      <div className="flex-1 min-w-0">
        <p className={nameClass} title={name}>
          {truncateMiddle(name)}
        </p>
        <div className={`${metaClass} mt-0.5 flex items-center gap-1`}>
          <span>{formatBytes(size)}</span>
          {subtitle && (
            <>
              <span>·</span>
              <span>{subtitle}</span>
            </>
          )}
        </div>
      </div>
      {ActionIcon && (
        <Button
          variant="ghost"
          size="sm"
          icon={ActionIcon}
          aria-label={actionLabel}
          {...(actionHref
            ? { as: 'a', href: actionHref, target: '_blank', rel: 'noopener noreferrer' }
            : { onClick: onAction })}
        />
      )}
    </div>
  );
}
